import { useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useReactToPrint } from 'react-to-print'
import useFetch from '../../hooks/useFetch'
import useIsMobile from '../../hooks/useIsMobile'
import Page from '../../components/Page'
import PageTitle from '../../components/PageTitle'
import Sheet from '../../components/Sheet'
import FindPieceFilters, { EMPTY_FILTERS, TOLERANCE_STEPS, hasAnyFilter, unsetFilterNames, parsedWeight } from './FindPieceFilters'
import FindPieceResults from './FindPieceResults'
import ProductSheet from '../products/ProductSheet'
import CreateProduct from '../products/CreateProduct'
import Tags from '../products/Tags'
import toEditProduct from '../products/toEditProduct'
import Lightbox from '../../components/Lightbox'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'

// The sticky column's width at 1280px, per the approved examples.
const PANEL_WIDTH = 336

// One row per applied filter -- "the filters as a sentence" -- built fresh on
// every render from the current filters, not stored: there is nothing here
// that isn't already derivable from `filters` and `tolerance`.
function buildChips(filters, tolerance, t) {
    const chips = []
    const weight = parsedWeight(filters.weight)

    if (weight !== null) {
        chips.push({
            key: 'weight',
            label: `${weight.toFixed(2)} g ${t('findPiece.tolerance', { percent: Math.round(tolerance * 100) })}`,
            apply: f => ({ ...f, weight: EMPTY_FILTERS.weight }),
        })
    }
    if (filters.category) {
        chips.push({ key: 'category', label: filters.category, apply: f => ({ ...f, category: EMPTY_FILTERS.category }) })
    }
    if (filters.metal) {
        chips.push({ key: 'metal', label: filters.metal, apply: f => ({ ...f, metal: EMPTY_FILTERS.metal }) })
    }
    filters.colors.forEach(color => {
        chips.push({ key: `color-${color}`, label: color, apply: f => ({ ...f, colors: f.colors.filter(c => c !== color) }) })
    })
    filters.stones.forEach(stone => {
        chips.push({ key: `stone-${stone}`, label: stone, apply: f => ({ ...f, stones: f.stones.filter(s => s !== stone) }) })
    })
    if (String(filters.priceMin).trim()) {
        chips.push({
            key: 'priceMin',
            label: `${t('findPiece.priceMin')} $${filters.priceMin}`,
            apply: f => ({ ...f, priceMin: EMPTY_FILTERS.priceMin }),
        })
    }
    if (String(filters.priceMax).trim()) {
        chips.push({
            key: 'priceMax',
            label: `${t('findPiece.priceMax')} $${filters.priceMax}`,
            apply: f => ({ ...f, priceMax: EMPTY_FILTERS.priceMax }),
        })
    }
    if (filters.location) {
        chips.push({
            key: 'location',
            label: t(filters.location === 'newark' ? 'findPiece.atNewark' : 'findPiece.atLongBranch'),
            apply: f => ({ ...f, location: EMPTY_FILTERS.location }),
        })
    }

    return chips
}

// A pill built from Box + IconButton rather than MUI's Chip: Chip's own
// onDelete renders a bare SVG with no button role, which a clerk's assistive
// tech (and this page's own tests) cannot address as a control.
function FilterChip({ label, onRemove, removeLabel }) {
    return (
        <Box
            sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.5,
                bgcolor: 'action.selected', borderRadius: 999, pl: 1.5, pr: 0.5, py: 0.25,
            }}
        >
            <Typography variant='body2' sx={{ fontVariantNumeric: 'tabular-nums' }}>{label}</Typography>
            <IconButton size='small' aria-label={removeLabel} onClick={onRemove}>
                <CloseIcon fontSize='inherit' />
            </IconButton>
        </Box>
    )
}

export default function FindPiece(props) {
    const { setAuth, fetchResource: injectedFetch, vendors = [] } = props
    const { t } = useTranslation()
    const fetch = useFetch({ setAuth })
    // Injectable so a test can drive the page without mocking the module.
    const fetchResource = injectedFetch || fetch.fetchResource
    const { isPending } = fetch
    const isMobile = useIsMobile()

    const [filters, setFilters] = useState(EMPTY_FILTERS)
    const [tolerance, setTolerance] = useState(TOLERANCE_STEPS[0])
    const [results, setResults] = useState(null)
    const [error, setError] = useState(null)
    // The weight the results on screen were found with -- not the field, which
    // the clerk may have edited since. The deltas are measured against this.
    const [searchedWeight, setSearchedWeight] = useState(null)
    // Decision 4: only meaningful once the phone view has folded the panel.
    const [filtersOpen, setFiltersOpen] = useState(false)
    // What a tap on a tile found -- the sheet's open condition below is what
    // makes it appear on the tap itself and fill in, rather than leaving the
    // grid looking inert while the lookup is in flight.
    const [found, setFound] = useState(null)
    const [lightboxImage, setLightboxImage] = useState(null)
    // Mirrors Products.js's lookUp: a failed products/get-product call must say
    // so, not just let ProductSheet's open condition flicker shut with nothing
    // shown.
    const [lookupError, setLookupError] = useState(null)
    // The tag payload the hidden <Tags> renders for the printer, and its own
    // inline failure report -- not a native alert(), which is the pattern
    // CRITICAL-FINDINGS.md finding 15 exists to retire.
    const [tagProducts, setTagProducts] = useState([])
    const [printError, setPrintError] = useState(null)
    // The piece being edited, already in CreateProduct's { fields, metafields }
    // shape. Set, it replaces the whole page with the form -- same as Products.
    const [editProduct, setEditProduct] = useState(null)
    const tagsRef = useRef()

    async function runSearch(nextFilters, nextTolerance) {
        setError(null)

        const response = await fetchResource({
            resource: 'products/find',
            body: { ...nextFilters, tolerance: nextTolerance },
            loadingKey: 'find'
        })

        if (response.error) {
            setResults(null)
            return setError(t('findPiece.searchError', { message: response.error }))
        }

        setResults({ matches: response.matches, total: response.total, limit: response.limit })
        // Must agree with the server's own notion of "a weight was searched"
        // (sql.js requires > 0): a weight the server dropped, e.g. '0', must not
        // leave every tile printing a delta against it.
        setSearchedWeight(parsedWeight(nextFilters.weight))
    }

    function handleSearch() {
        // Every search starts tight. Carrying a widened band over to a fresh
        // search would silently loosen a query the clerk thinks is precise.
        setTolerance(TOLERANCE_STEPS[0])
        runSearch(filters, TOLERANCE_STEPS[0])
        setFiltersOpen(false)
    }

    function handleWiden() {
        const next = TOLERANCE_STEPS[TOLERANCE_STEPS.indexOf(tolerance) + 1]
        if (!next) return
        setTolerance(next)
        runSearch(filters, next)
        // Widening from inside the reopened Filters sheet must surface its own
        // result, not leave it behind a still-open modal -- same as Search.
        setFiltersOpen(false)
    }

    function handleClear() {
        setFilters(EMPTY_FILTERS)
        setTolerance(TOLERANCE_STEPS[0])
        setResults(null)
        setError(null)
        setSearchedWeight(null)
    }

    // A pill's X removes one value and immediately re-runs the search: on a
    // folded phone panel it is the only control left to act on, so it has to
    // stand in for both "change a filter" and "search again". Emptying the
    // last filter this way mirrors Clear rather than asking the server for a
    // request it would answer NO_FILTERS.
    function handleRemoveChip(chip) {
        const next = chip.apply(filters)
        setFilters(next)
        setTolerance(TOLERANCE_STEPS[0])
        if (hasAnyFilter(next)) {
            runSearch(next, TOLERANCE_STEPS[0])
        } else {
            setResults(null)
            setError(null)
            setSearchedWeight(null)
        }
    }

    // Same lookup Products.js uses for a scan: the mirror match is a summary,
    // and ProductSheet reads get-product's fuller shape.
    async function handleSelect(match) {
        setLookupError(null)
        const response = await fetchResource({
            resource: 'products/get-product',
            body: { barcode: match.barcode },
            loadingKey: 'lookup'
        })

        if (response.error) {
            return setLookupError(t('products.notFound', { barcode: match.barcode }))
        }

        setFound({ ...response.product, barcode: match.barcode })
    }

    function handleImageClick(image) {
        setLightboxImage(image)
    }

    // The hook rather than the <ReactToPrint> component, same as Products: the
    // print control is an ordinary ActionBar button, not a render prop's child.
    const printTags = useReactToPrint({ content: () => tagsRef.current })

    // Same shape as Products.handlePrintFound: ask the backend for the tag
    // payload rather than reusing the match tile, whose mirror row carries a
    // summary, not the metafields a tag is built from.
    async function handlePrint(product) {
        setPrintError(null)
        const response = await fetchResource({
            resource: 'products/get-tag-info',
            body: { barcodes: [product.barcode] },
            blocking: true
        })

        if (response.error) {
            return setPrintError(t('findPiece.printError', { message: response.error }))
        }

        setTagProducts(response.products)
        setFound(null)
        printTags()
    }

    // The sheet already holds get-product's fuller shape, so the handoff is the
    // same one Products makes -- through the one shared mapping.
    function handleEdit(product) {
        setEditProduct(toEditProduct(product))
        setFound(null)
    }

    // What CreateProduct calls after a save lands: re-run the search on screen
    // so the grid reflects the edit, the way Products refetches its list.
    async function refreshResults() {
        if (results !== null && hasAnyFilter(filters)) {
            await runSearch(filters, tolerance)
        }
    }

    const loading = isPending('find')
    const chips = buildChips(filters, tolerance, t)
    // Decision 4: at 390px, once there is something to show, the form gives
    // its space to the result of using it. At 1280px this stays false always.
    const panelFolded = isMobile && results !== null

    // Mirrors FindPieceFilters' own gate for its inline Widen: nothing to widen
    // to without a weight, or once the last TOLERANCE_STEPS entry is reached.
    const nextTolerance = TOLERANCE_STEPS[TOLERANCE_STEPS.indexOf(tolerance) + 1]
    const canWidenStandalone = parsedWeight(filters.weight) !== null && Boolean(nextTolerance)

    const filtersPanel = (
        <FindPieceFilters
            filters={filters}
            onChange={setFilters}
            onSearch={handleSearch}
            onClear={handleClear}
            canSearch={hasAnyFilter(filters)}
            tolerance={tolerance}
            onWiden={handleWiden}
            loading={loading}
        />
    )

    const firstUnsetFilter = unsetFilterNames(filters, t)[0]

    // The form takes the whole screen in place of the page, exactly as it does
    // on Products. FindPiece itself stays mounted, so the filters, results and
    // scroll position are all still here when Save or Cancel comes back.
    if (editProduct) {
        return (
            <CreateProduct
                showCreateProduct={Boolean(editProduct)}
                setShowCreateProduct={open => { if (!open) setEditProduct(null) }}
                // Selection is a Products concern; the form clears it on cancel
                // and close, so it needs somewhere for that call to land.
                setTargetBarcodes={() => {}}
                fetchResource={fetchResource}
                refreshProducts={refreshResults}
                edit={true}
                product={editProduct}
                manageProductTitle={t('products.editProduct')}
                vendors={vendors}
            />
        )
    }

    return (
        <Page>
            <PageTitle>{t('findPiece.title')}</PageTitle>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 2, textAlign: 'center' }}>
                {t('findPiece.intro')}
            </Typography>

            {error && <Alert severity='error' sx={{ mb: 2 }}>{error}</Alert>}
            {lookupError && (
                <Alert severity='error' sx={{ mb: 2 }} onClose={() => setLookupError(null)}>
                    {lookupError}
                </Alert>
            )}
            {printError && (
                <Alert severity='error' sx={{ mb: 2 }} onClose={() => setPrintError(null)}>
                    {printError}
                </Alert>
            )}

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 0, sm: 5 }, alignItems: 'flex-start' }}>
                {!panelFolded && (
                    <Box sx={{ width: { xs: '100%', sm: PANEL_WIDTH }, flex: 'none', position: { sm: 'sticky' }, top: { sm: 24 } }}>
                        {filtersPanel}
                    </Box>
                )}

                <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                    {panelFolded && (
                        <>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
                                {chips.map(chip => (
                                    <FilterChip
                                        key={chip.key}
                                        label={chip.label}
                                        removeLabel={t('findPiece.removeFilter', { filter: chip.label })}
                                        onRemove={() => handleRemoveChip(chip)}
                                    />
                                ))}
                                <Button size='small' variant='outlined' onClick={() => setFiltersOpen(true)}>
                                    {t('findPiece.filtersButton')}
                                </Button>
                            </Box>
                            <Sheet open={filtersOpen} onClose={() => setFiltersOpen(false)} title={t('findPiece.title')}>
                                {filtersPanel}
                            </Sheet>
                        </>
                    )}

                    {results && (
                        <>
                            {results.total > results.matches.length && (
                                <Typography variant='body2' sx={{ my: 2 }}>
                                    <Box component='span' sx={{ color: 'warning.main', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                                        {/* With no weight searched the server orders by
                                            updated_at DESC, not by closeness -- the 50
                                            shown are an arbitrary recent slice, and the
                                            banner must not claim otherwise. */}
                                        {t(searchedWeight !== null ? 'findPiece.tooMany' : 'findPiece.tooManyRecent', {
                                            total: results.total, limit: results.limit,
                                        })}
                                    </Box>
                                    {firstUnsetFilter && (
                                        <Box component='span' sx={{ display: 'block', color: 'text.secondary' }}>
                                            {t('findPiece.tooManyAddFilter', { filter: firstUnsetFilter })}
                                        </Box>
                                    )}
                                </Typography>
                            )}

                            {results.total === 0 && (
                                <Box sx={{ mt: 1 }}>
                                    <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 1 }}>
                                        {t('findPiece.noResults')}
                                    </Typography>
                                    <Typography variant='body2' color='text.secondary'>
                                        {t('findPiece.noResultsHint')}
                                    </Typography>
                                    {/* Decision 4 / the approved zero-results mocks (both phoneZero()
                                        and the desktop state): Widen must be reachable at zero taps
                                        from the zero block itself, not only from the sticky panel's
                                        own inline copy -- the panel carries every applied filter, but
                                        this is the screen the spec calls "the worst outcome", and the
                                        design's own note says it must never dead-end. Gated on there
                                        being a weight to widen and a step left, same as the inline
                                        Widen -- an unconditional button here would be a dead control. */}
                                    {canWidenStandalone && (
                                        <Button
                                            type='button'
                                            variant='outlined'
                                            onClick={handleWiden}
                                            sx={{ mt: 1.5 }}
                                        >
                                            {t('findPiece.widenTo', { percent: Math.round(nextTolerance * 100) })}
                                        </Button>
                                    )}
                                    {searchedWeight !== null && (
                                        <Typography variant='caption' color='text.secondary' component='p' sx={{ mt: 1.5 }}>
                                            {t('findPiece.noWeightNote')}
                                        </Typography>
                                    )}
                                </Box>
                            )}

                            {results.total > 0 && results.total <= results.matches.length && (
                                <Typography variant='body2' color='text.secondary' sx={{ my: 2 }}>
                                    {/* "closest weight first" is only true when a weight was
                                        actually searched -- same condition as the over-50 banner
                                        above. */}
                                    {t(searchedWeight !== null ? 'findPiece.resultsWeighted' : 'findPiece.results', {
                                        count: results.total,
                                    })}
                                </Typography>
                            )}

                            {results.total > 0 && (
                                <FindPieceResults
                                    matches={results.matches}
                                    searchedWeight={searchedWeight}
                                    onSelect={handleSelect}
                                    onImageClick={handleImageClick}
                                />
                            )}
                        </>
                    )}
                </Box>
            </Box>

            {/* Print leads here, the reverse of Products' emphasis: a piece is
                looked up on this page precisely because it has no readable tag,
                so printing one is the likeliest next act, with Edit beside it. */}
            <ProductSheet
                open={isPending('lookup') || Boolean(found)}
                loading={isPending('lookup')}
                onClose={() => setFound(null)}
                product={found}
                onImageClick={handleImageClick}
                onEdit={handleEdit}
                onPrint={handlePrint}
                primaryAction='print'
            />

            {/* What the printer prints; never on screen. */}
            <div style={{ display: 'none' }}>
                <Tags products={tagProducts} ref={tagsRef} />
            </div>

            <Lightbox
                open={Boolean(lightboxImage)}
                onClose={() => setLightboxImage(null)}
                src={lightboxImage}
                alt='product'
            />
        </Page>
    )
}
